import { useQuery } from '@tanstack/react-query';
import type { TesterPresenceTimingConfig } from '@runhuman/shared';
import { fetchActivityConfig } from './api';

/**
 * Polls `/discord/activity/config` — mirror of the web Tester Portal's
 * `useInactivityConfig` (which hits `/tester/config`). Returns the
 * deployment-wide presence timing thresholds (`inactiveDays`, sweep windows,
 * warning minutes) so the Activity's presence ctx matches what
 * `runhuman.com/tester` and `/admin/home` compute against.
 *
 * Values are deployment-static — cached for an hour, treated as a one-shot
 * fetch per session. Consumers should treat the result as nullable until
 * the initial fetch resolves and use a sensible derivation context only
 * once it has loaded (the bucketing is meaningless without the thresholds
 * matching the server's idea of "stale").
 */
export function useActivityConfig(accessToken: string | undefined): {
  data: TesterPresenceTimingConfig | undefined;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ['discord-activity-config'],
    queryFn: () => fetchActivityConfig(accessToken!),
    enabled: Boolean(accessToken),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  return {
    data: query.data?.presence,
    isLoading: query.isLoading,
  };
}
